/**
 * The standard ticket fields: the columns every ticket has, with app logic
 * behind them, as opposed to admin-added custom fields (it_ticket_field_defs).
 * Field Settings lists both. For a standard field an admin can change its
 * label and help text, and for the optional ones whether it is required and
 * whether the New ticket form shows it (00590 stores only what was changed).
 *
 * What each field allows is decided here, not by the stored row: a core field
 * stays required and shown whatever a row says, so a bad row can't break
 * filing a ticket.
 */
import { TICKET_PRIORITIES, TICKET_STATUSES } from '../types/ticket'

export const STANDARD_FIELD_KEYS = [
  'customer', 'title', 'description', 'category', 'priority', 'status',
  'requester', 'assignee', 'due_date', 'attachments',
] as const

export type StandardFieldKey = (typeof STANDARD_FIELD_KEYS)[number]

/** When the New ticket form shows the field, if an admin can't change it. */
export type FixedOnCreate = 'always' | 'never' | 'when-choice'

export interface StandardFieldDef {
  key: StandardFieldKey
  defaultLabel: string
  typeLabel: string
  /** What the field is, shown under its name on Field Settings. */
  about: string
  /** Optional fields can be made required and hidden on the New ticket form. */
  optional: boolean
  /** For a core field: when the New ticket form shows it. */
  fixedOnCreate?: FixedOnCreate
  /** Values the app depends on, so they can't be edited here. */
  fixedOptions?: string
  /** Category's options are managed on its row. */
  editableOptions?: boolean
}

export const STANDARD_FIELDS: readonly StandardFieldDef[] = [
  { key: 'customer', defaultLabel: 'Customer', typeLabel: 'Customer', about: 'Who the ticket is for. Asked only when the person filing can choose more than one.', optional: false, fixedOnCreate: 'when-choice' },
  { key: 'title', defaultLabel: 'Title', typeLabel: 'Text', about: 'One-line summary shown in the queue.', optional: false, fixedOnCreate: 'always' },
  { key: 'description', defaultLabel: 'Description', typeLabel: 'Long text', about: 'The full request.', optional: false, fixedOnCreate: 'always' },
  { key: 'category', defaultLabel: 'Category', typeLabel: 'Dropdown', about: 'What kind of request it is. Workflow rules and reports group by it.', optional: false, fixedOnCreate: 'always', editableOptions: true },
  { key: 'priority', defaultLabel: 'Priority', typeLabel: 'Dropdown', about: 'Set by the priority questions on the New ticket form.', optional: false, fixedOnCreate: 'always', fixedOptions: [...TICKET_PRIORITIES].reverse().join(', ') },
  { key: 'status', defaultLabel: 'Status', typeLabel: 'Dropdown', about: 'Where the ticket is in the workflow. New tickets start as New.', optional: false, fixedOnCreate: 'never', fixedOptions: TICKET_STATUSES.join(', ') },
  { key: 'requester', defaultLabel: 'Created for', typeLabel: 'Person', about: 'Who the ticket is for. Agents and admins can file for someone else.', optional: false, fixedOnCreate: 'always' },
  { key: 'assignee', defaultLabel: 'Assignee', typeLabel: 'Person', about: 'Who is working the ticket.', optional: true },
  { key: 'due_date', defaultLabel: 'Due date', typeLabel: 'Date', about: 'When the ticket should be done. Past due and still open counts as overdue.', optional: true },
  { key: 'attachments', defaultLabel: 'Attachments', typeLabel: 'Files', about: 'Screenshots, logs, and documents added when filing.', optional: true },
]

export const STANDARD_FIELD_BY_KEY = Object.fromEntries(STANDARD_FIELDS.map((f) => [f.key, f])) as Record<StandardFieldKey, StandardFieldDef>

export function isStandardFieldKey(v: string): v is StandardFieldKey {
  return (STANDARD_FIELD_KEYS as readonly string[]).includes(v)
}

/** One stored row (00590): null means "use the default". */
export interface StandardFieldSetting {
  key: StandardFieldKey
  label: string | null
  helpText: string | null
  required: boolean | null
  showOnCreate: boolean | null
}

export interface ResolvedStandardField {
  def: StandardFieldDef
  key: StandardFieldKey
  label: string
  helpText: string | null
  required: boolean
  showOnCreate: boolean
  /** True when any setting differs from the default, so Reset has something to do. */
  customized: boolean
}

export type ResolvedStandardFields = Record<StandardFieldKey, ResolvedStandardField>

export function resolveStandardFields(settings: readonly StandardFieldSetting[]): ResolvedStandardFields {
  const byKey = new Map(settings.map((s) => [s.key, s]))
  const out = {} as ResolvedStandardFields
  for (const def of STANDARD_FIELDS) {
    const s = byKey.get(def.key)
    const label = s?.label?.trim() || def.defaultLabel
    const helpText = s?.helpText?.trim() || null
    // An optional field can't be required while hidden: nobody could fill it.
    const showOnCreate = def.optional ? s?.showOnCreate ?? true : def.fixedOnCreate !== 'never'
    const required = def.optional ? showOnCreate && (s?.required ?? false) : def.key !== 'status'
    out[def.key] = {
      def, key: def.key, label, helpText, required, showOnCreate,
      customized: label !== def.defaultLabel || helpText != null || (def.optional && (s?.required === true || s?.showOnCreate === false)),
    }
  }
  return out
}

/** The form hint: "Optional" for an optional field that isn't required, then the admin's help text. */
export function standardFieldHint(f: ResolvedStandardField, fallback?: string): string | undefined {
  const parts = [f.def.optional && !f.required ? 'Optional' : null, f.helpText ?? fallback ?? null].filter(Boolean)
  return parts.length > 0 ? parts.join('. ') : undefined
}
